import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  CardMedia,
  CircularProgress,
  Alert,
} from "@mui/material";
import { postData } from "../../Webservice/Webservice";

const placeholderImage = "https://via.placeholder.com/250x250?text=No+Image";

function Subcategory() {
  const location = useLocation();
  const navigate = useNavigate();
  const { categoryId, categoryTitle } = location.state || {};

  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const prettyName = categoryTitle || "Subcategory";

  useEffect(() => {
    // If accessed directly without categoryId
    if (!categoryId) {
      setLoading(false);
      setError("Invalid category. Please go back and select a category again.");
      return;
    }

    const fetchSubcategories = async () => {
      try {
        setLoading(true);
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await postData(
          "subcategory/list",
          { category_id: categoryId },
          token
        );

        if (response && Array.isArray(response.subcategories)) {
          setSubcategories(response.subcategories);
        } else {
          setSubcategories([]);
        }
      } catch (err) {
        console.error("Error fetching subcategories:", err);
        setError("Failed to load subcategories. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchSubcategories();
  }, [categoryId]);

  // Loading state
  if (loading) {
    return (
      <Container
        sx={{
          py: { xs: 6, md: 10 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: "#f7f7f7", minHeight: "100vh", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Title */}
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: "24px", sm: "32px", md: "40px" },
              color: "#222",
              fontWeight: 900,
              lineHeight: 1.1,
              letterSpacing: "3px",
              textTransform: "uppercase",
              textAlign: "center",
              mb: 1,
            }}
          >
            {prettyName}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: "center",
              maxWidth: 480,
              mx: "auto",
            }}
          >
            Explore curated subcategories under this category.
          </Typography>
        </Box>

        {/* Subcategories Grid */}
        {subcategories.length === 0 ? (
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ py: 5 }}
          >
            No subcategories found.
          </Typography>
        ) : (
          <Grid
            container
            spacing={{ xs: 2, sm: 3, md: 4 }}
            justifyContent="center"
          >
            {subcategories.map((subcategory) => (
              <Grid
                item
                key={subcategory._id}
                xs={6}      // 2 per row on mobile
                sm={4}      // 3 per row on small screens
                md={3}      // 4 per row on medium+
              >
                <Box
                  sx={{
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const categorySlug =
                      categoryTitle
                        ?.toLowerCase()
                        .replace(/\s+/g, "-") || "products";
                    const subcategorySlug =
                      subcategory.title
                        ?.toLowerCase()
                        .replace(/\s+/g, "-") || "all";

                    navigate(`/${categorySlug}/${subcategorySlug}`, {
                      state: {
                        categoryId,
                        subcategoryId: subcategory._id,
                        subcategoryTitle: subcategory.title,
                      },
                    });
                  }}
                >
                  {/* Circular Image */}
                  <Box
                    sx={{
                      width: { xs: 120, sm: 150, md: 180 },
                      height: { xs: 120, sm: 150, md: 180 },
                      borderRadius: "50%",
                      overflow: "hidden",
                      mx: "auto",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                      transition:
                        "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={subcategory.image || placeholderImage}
                      alt={subcategory.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = placeholderImage;
                      }}
                    />
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mt: 2,
                      fontWeight: 600,
                      fontSize: { xs: "13px", sm: "15px", md: "16px" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: { xs: 120, sm: 150, md: 180 },
                      mx: "auto",
                    }}
                    title={subcategory.title}
                  >
                    {subcategory.title}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}

export default Subcategory;
